import { useMemo } from 'react';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import {
  canAccessContractorPayablesFinances,
  canAccessFullFinancesHub,
  canEnterContractorTimeOnBehalf,
  canManageContractorPayables,
  canWriteFinancesModule,
  resolveEmployeeDesignations,
} from '../utils/contractorPayablesAccess';

/** Contractor payables + full Finances hub access with CRM hydration fallback for EA designations. */
export function useContractorPayablesAccess() {
  const { user } = useAuth();
  const { crmUsers } = useCRM();

  const designations = useMemo(
    () => resolveEmployeeDesignations(user, crmUsers),
    [crmUsers, user],
  );

  const briefingProfile = useMemo(() => {
    const fromAuth = user?.executiveBriefing;
    const fromCrm = crmUsers.find((u) => u.uid === user?.id)?.executiveBriefing;
    const cfg = fromAuth?.enabled ? fromAuth : fromCrm?.enabled ? fromCrm : undefined;
    if (!cfg?.enabled) return undefined;
    if (!cfg.profile || cfg.profile === 'coo') return 'coo' as const;
    return cfg.profile;
  }, [crmUsers, user]);

  const accessArgs = useMemo(
    () => ({
      authRole: user?.role,
      designations,
      briefingProfile,
    }),
    [user?.role, designations, briefingProfile],
  );

  const canAccess = canAccessContractorPayablesFinances(accessArgs);
  const canManage = canManageContractorPayables(accessArgs);
  const canOnBehalf = canEnterContractorTimeOnBehalf(accessArgs);
  const canFullFinances = canAccessFullFinancesHub(accessArgs);
  const canWriteFinances = canWriteFinancesModule(accessArgs);

  return { canAccess, canManage, canOnBehalf, canFullFinances, canWriteFinances, designations };
}
